import GraduatedStudents from '../components/GraduatedStudents';
import { PageHeader, LoadingState, DataTable } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  Chip,
  Container,
  Avatar,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  useMediaQuery,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert, Tabs, Tab
} from '@mui/material';
import {
  ExpandMore,
  School,
  Boy,
  Girl,
  Person,
  Groups,
  People,
  Close as CloseIcon,
  Phone,
  Print as PrintIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../utils/Api';

export default function EnrollmentPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [enrollmentData, setEnrollmentData] = useState({
    summary: {
      totalClasses: 0,
      totalStudents: 0,
      totalBoys: 0,
      totalGirls: 0
    },
    classes: []
  });
  const [section, setSection] = useState('enrolled');
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  
  // Modal state
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Move student state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [targetClassId, setTargetClassId] = useState('');
  const [movingStudent, setMovingStudent] = useState(false);

  // Delete class state
  const [deleteClassConfirm, setDeleteClassConfirm] = useState(null);
  const [deletingClass, setDeletingClass] = useState(false);

  const attachClassToStudents = useCallback((classesData = []) =>
    classesData.map((cls) => {
      const classDisplayName = cls.classDisplayName || cls.className || cls.name;
      const classId = cls.classId || cls._id;

      return {
        ...cls,
        classDisplayName,
        students: (cls.students || [])
          .map((student) => ({
            ...student,
            currentClassId: classId,
            currentClassName: classDisplayName
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      };
    }).sort((a, b) => (a.classDisplayName || '').localeCompare(b.classDisplayName || '')), []);

  const handleOpenStudentModal = (student) => {
    setSelectedStudent(student);
    setModalOpen(true);
  };

  const handleCloseStudentModal = () => {
    setModalOpen(false);
    setSelectedStudent(null);
  };

  const handleOpenMoveDialog = () => {
    if (!selectedStudent) return;
    setTargetClassId('');
    setMoveDialogOpen(true);
  };

  const handleCloseMoveDialog = () => {
    if (movingStudent) return;
    setMoveDialogOpen(false);
    setTargetClassId('');
  };

  const handleMoveStudent = async () => {
    if (!selectedStudent || !targetClassId) {
      toast.error('Please select the class to move the student to.');
      return;
    }

    if (targetClassId === String(selectedStudent.currentClassId || '')) {
      toast.info('Student is already in the selected class.');
      return;
    }

    try {
      setMovingStudent(true);
      const res = await api.patch(`/enrollments/students/${selectedStudent._id}/class`, {
        classId: targetClassId
      });

      toast.success(res.data?.message || 'Student moved successfully.');
      setMoveDialogOpen(false);
      setModalOpen(false);
      setSelectedStudent(null);
      setTargetClassId('');
      await fetchSummary();
    } catch (error) {
      console.error('❌ Error moving student:', error);
      toast.error(error.response?.data?.message || 'Failed to move student.');
    } finally {
      setMovingStudent(false);
    }
  };

  const handlePrintClassList = (cls) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }

    const className = cls.classDisplayName || cls.className || cls.name || 'Class';
    const boysCount = cls.boys || 0;
    const girlsCount = cls.girls || 0;
    const totalCount = cls.total || (boysCount + girlsCount);

    const students = cls.students || [];
    const males = students.filter(s => (s.gender || '').toLowerCase() === 'male');
    const females = students.filter(s => (s.gender || '').toLowerCase() === 'female');
    const others = students.filter(s => {
      const g = (s.gender || '').toLowerCase();
      return g !== 'male' && g !== 'female';
    });

    let htmlRows = '';
    let counter = 1;

    const appendGroup = (groupTitle, groupStudents) => {
      if (groupStudents.length === 0) return;
      htmlRows += `
        <tr style="background-color: #ededed;">
          <td colspan="5" style="text-align: center; font-weight: bold; font-size: 14px; letter-spacing: 1px; text-transform: uppercase;">
            ${groupTitle} (${groupStudents.length})
          </td>
        </tr>
      `;
      groupStudents.forEach((student) => {
        const gender = student.gender || 'N/A';
        const genderClass = gender.toLowerCase() === 'male' ? 'male' : (gender.toLowerCase() === 'female' ? 'female' : '');
        htmlRows += `
          <tr>
            <td>${counter++}</td>
            <td><strong>${student.name || 'N/A'}</strong></td>
            <td><span class="gender-chip ${genderClass}">${gender}</span></td>
            <td>${student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'N/A'}</td>
            <td>${student.admissionNumber || 'N/A'}</td>
          </tr>
        `;
      });
    };

    appendGroup('Boys', males);
    appendGroup('Girls', females);
    appendGroup('Other', others);

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${className} - Class List</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            h2 { text-align: center; margin-bottom: 5px; text-transform: uppercase; }
            .subtitle { text-align: center; font-size: 14px; color: #555; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
            th { background-color: #f8f9fa; font-weight: bold; border-bottom: 2px solid #ccc; }
            tr:nth-child(even) { background-color: #fdfdfd; }
            .gender-chip { padding: 4px 8px; border-radius: 12px; font-size: 12px; font-weight: bold; }
            .male { background-color: #e3f2fd; color: #0d47a1; }
            .female { background-color: #fce4ec; color: #880e4f; }
            @media print {
              body { padding: 0; }
              table { font-size: 12px; }
              th { background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; }
              .male { background-color: #e3f2fd !important; -webkit-print-color-adjust: exact; }
              .female { background-color: #fce4ec !important; -webkit-print-color-adjust: exact; }
              tr[style] { background-color: #ededed !important; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <h2>${className} - Enrolled Students</h2>
          <div class="subtitle">
            Total Students: <strong>${totalCount}</strong> &nbsp;|&nbsp; 
            Boys: <strong>${boysCount}</strong> &nbsp;|&nbsp; 
            Girls: <strong>${girlsCount}</strong>
          </div>
          <table>
            <thead>
              <tr>
                <th width="5%">#</th>
                <th width="35%">Name</th>
                <th width="15%">Gender</th>
                <th width="20%">Date of Birth</th>
                <th width="25%">Admission No.</th>
              </tr>
            </thead>
            <tbody>
              ${htmlRows}
            </tbody>
          </table>
          <script>
            window.onload = function() { 
              setTimeout(function() { 
                window.print(); 
              }, 500); 
            }
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDeleteClassClick = (cls) => {
    setDeleteClassConfirm(cls);
  };

  const handleDeleteClassConfirm = async () => {
    if (!deleteClassConfirm) return;
    
    try {
      setDeletingClass(true);
      const res = await api.delete(`/students/class/${deleteClassConfirm.classId}`);
      toast.success(res.data.message || 'Successfully deleted all students in the class.');
      setDeleteClassConfirm(null);
      fetchSummary(); // Refresh data
    } catch (error) {
      console.error('❌ Error deleting class students:', error);
      toast.error(error.response?.data?.message || 'Failed to delete students.');
    } finally {
      setDeletingClass(false);
    }
  };

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/enrollments/summary');
      // Handle both response structures for backward compatibility
      if (res.data.success && res.data.classes) {
        setEnrollmentData({
          summary: res.data.summary || {
            totalClasses: res.data.classes.length,
            totalStudents: res.data.classes.reduce((sum, cls) => sum + (cls.boys || 0) + (cls.girls || 0), 0),
            totalBoys: res.data.classes.reduce((sum, cls) => sum + (cls.boys || 0), 0),
            totalGirls: res.data.classes.reduce((sum, cls) => sum + (cls.girls || 0), 0)
          },
          classes: attachClassToStudents(res.data.classes)
        });
      } else if (Array.isArray(res.data)) {
        // Fallback for old response format
        setEnrollmentData({
          summary: {
            totalClasses: res.data.length,
            totalStudents: res.data.reduce((sum, cls) => sum + (cls.boys || 0) + (cls.girls || 0), 0),
            totalBoys: res.data.reduce((sum, cls) => sum + (cls.boys || 0), 0),
            totalGirls: res.data.reduce((sum, cls) => sum + (cls.girls || 0), 0)
          },
          classes: attachClassToStudents(res.data)
        });
      } else {
        setEnrollmentData({
          summary: res.data.summary || { totalClasses: 0, totalStudents: 0, totalBoys: 0, totalGirls: 0 },
          classes: attachClassToStudents(res.data.classes || [])
        });
      }
    } catch (err) {
      console.error('❌ Error fetching enrollment summary:', err);
      toast.error('Failed to load enrollment data');
    } finally {
      setLoading(false);
    }
  }, [attachClassToStudents]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await api.get('/classes');
        const responseData = res.data;
        const loadedClasses = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData.classes)
            ? responseData.classes
            : [];

        setClasses(
          loadedClasses
            .map((cls) => ({
              ...cls,
              classDisplayName: cls.displayName || cls.classDisplayName || cls.name
            }))
            .sort((a, b) => (a.classDisplayName || '').localeCompare(b.classDisplayName || ''))
        );
      } catch (err) {
        console.error('❌ Error fetching classes:', err);
        toast.error('Failed to load class list');
      }
    };

    fetchSummary();
    fetchClasses();
  }, [fetchSummary]);

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';

  // Mobile Student Card Component
  const MobileStudentCard = ({ student, index }) => (
    <Card 
       sx={{ mb: 1, boxShadow: 1, border: `1px solid ${theme.palette.divider}`, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
       onClick={() => handleOpenStudentModal(student)}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header with Student Info */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <StudentAvatar student={student}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 32,
                  height: 32,
                  fontSize: '0.75rem'
                }}
              >
                {getInitials(student.name || 'N/A')}
              </StudentAvatar>
              <Box>
                <Typography variant="subtitle2" fontWeight="600">
                  {student.name || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{index + 1}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Student Details */}
          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                DATE OF BIRTH
              </Typography>
              <Typography variant="body2">
                {student.dateOfBirth
                  ? new Date(student.dateOfBirth).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                ADMISSION NUMBER
              </Typography>
              <Chip
                label={student.admissionNumber || 'N/A'}
                size="small"
                color="default"
                variant="outlined"
              />
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  // Summary Stats Cards
  const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <SummaryMetric sx={{
      boxShadow: isMobile ? 1 : 3,
      border: `1px solid ${color}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: isMobile ? 'none' : 'translateY(-4px)',
        boxShadow: isMobile ? 2 : 6
      }
    }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{
            bgcolor: color,
            width: isMobile ? 36 : 48,
            height: isMobile ? 36 : 48
          }}>
            <Icon fontSize={isMobile ? "small" : "medium"} />
          </Avatar>
          <Box>
            <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
              {title}
            </Typography>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}>
              {value?.toLocaleString() || 0}
            </Typography>
            <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </SummaryMetric>
  );

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title="Enrollment" description="View current class enrollment and graduated students by year." />
      <Tabs value={section} onChange={(_, value) => setSection(value)} variant="scrollable" scrollButtons="auto" aria-label="Enrollment sections" sx={{ mb: 3 }}>
        <Tab label="Class Enrollment" value="enrolled" id="enrolled-tab" aria-controls="enrolled-panel" />
        <Tab label="Graduated Students" value="graduates" id="graduates-tab" aria-controls="graduates-panel" />
      </Tabs>
      <Box role="tabpanel" id="graduates-panel" aria-labelledby="graduates-tab" hidden={section !== 'graduates'}>
        {section === 'graduates' && <GraduatedStudents onViewStudent={handleOpenStudentModal} />}
      </Box>
      <Box role="tabpanel" id="enrolled-panel" aria-labelledby="enrolled-tab" hidden={section !== 'enrolled'}>
      {!loading && enrollmentData.classes.length > 0 && (
        <SummaryStrip container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 3 : 4 }}>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <StatCard
              icon={Groups}
              title="Total Classes"
              value={enrollmentData.summary.totalClasses}
              color={theme.palette.primary.main}
              subtitle={`${enrollmentData.classes.length} active`}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <StatCard
              icon={People}
              title="Total Students"
              value={enrollmentData.summary.totalStudents}
              color={theme.palette.success.main}
              subtitle="All enrolled"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <StatCard
              icon={Boy}
              title="Boys"
              value={enrollmentData.summary.totalBoys}
              color={theme.palette.info.main}
              subtitle={`${enrollmentData.summary.totalStudents > 0 ? Math.round((enrollmentData.summary.totalBoys / enrollmentData.summary.totalStudents) * 100) : 0}%`}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <StatCard
              icon={Girl}
              title="Girls"
              value={enrollmentData.summary.totalGirls}
              color={theme.palette.secondary.main}
              subtitle={`${enrollmentData.summary.totalStudents > 0 ? Math.round((enrollmentData.summary.totalGirls / enrollmentData.summary.totalStudents) * 100) : 0}%`}
            />
          </Grid>
        </SummaryStrip>
      )}

      {/* Summary Statistics */}
      

      {loading ? (
        <LoadingState />
      ) : enrollmentData.classes.length === 0 ? (
        <Card sx={{
          textAlign: 'center',
          py: 6,
          boxShadow: isMobile ? 1 : 3
        }}>
          <CardContent>
            <Avatar sx={{
              bgcolor: alpha(theme.palette.text.secondary, 0.1),
              width: isMobile ? 56 : 64,
              height: isMobile ? 56 : 64,
              mx: 'auto',
              mb: 2
            }}>
              <Person fontSize={isMobile ? "medium" : "large"} />
            </Avatar>
            <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary" gutterBottom>
              No enrollment data found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are no students enrolled in any classes yet.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Class Enrollment Details ({enrollmentData.classes.length} Classes)
              </Typography>
            </Box>

            {enrollmentData.classes.map((cls) => (
              <Accordion
                key={cls.classId}
                sx={{
                  boxShadow: 'none',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:before': { display: 'none' },
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1.5 : 2,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    },
                    minHeight: '72px !important'
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Stack
                      direction={isMobile ? "column" : "row"}
                      alignItems={isMobile ? "flex-start" : "center"}
                      spacing={isMobile ? 1 : 2}
                      sx={{ flexGrow: 1 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexGrow: 1 }}>
                        <Avatar sx={{
                          bgcolor: theme.palette.primary.main,
                          width: isMobile ? 32 : 40,
                          height: isMobile ? 32 : 40
                        }}>
                          <School fontSize={isMobile ? "small" : "medium"} />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant={isMobile ? "subtitle2" : "subtitle1"} fontWeight="600">
                            {cls.classDisplayName || cls.className || cls.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cls.total || (cls.boys || 0) + (cls.girls || 0)} students
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        spacing={1}
                        alignItems={isMobile ? "flex-start" : "center"}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrintClassList(cls);
                          }}
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                          }}
                          title="Print Class List"
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                        {/* Delete Class button — hidden but kept for future use */}
                        {false && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClassClick(cls);
                          }}
                          sx={{ 
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                          }}
                          title="Delete All Students in Class"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        )}
                        <Chip
                          icon={<Boy fontSize={isMobile ? "small" : "medium"} />}
                          label={`Boys: ${cls.boys || 0}`}
                          color="info"
                          variant="outlined"
                          size={isMobile ? "small" : "medium"}
                          sx={{ fontWeight: 500 }}
                        />
                        <Chip
                          icon={<Girl fontSize={isMobile ? "small" : "medium"} />}
                          label={`Girls: ${cls.girls || 0}`}
                          color="secondary"
                          variant="outlined"
                          size={isMobile ? "small" : "medium"}
                          sx={{ fontWeight: 500 }}
                        />
                        <Chip
                          label={`Total: ${cls.total || (cls.boys || 0) + (cls.girls || 0)}`}
                          color="primary"
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            fontWeight: 600,
                            minWidth: isMobile ? 70 : 80
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <Paper className="workspace-section" sx={{
                    mx: isMobile ? 1 : 2,
                    mb: isMobile ? 1 : 2,
                    border: `1px solid ${theme.palette.divider}`
                  }}>
                    {isMobile ? (
                      // Mobile View - Cards
                      <Box sx={{ p: 2 }}>
                        {cls.students && cls.students.length > 0 ? (
                          <Stack spacing={1}>
                            {cls.students.map((student, index) => (
                              <MobileStudentCard
                                key={index}
                                student={student}
                                index={index}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Person sx={{
                              fontSize: 40,
                              color: 'text.secondary',
                              opacity: 0.5,
                              mb: 1
                            }} />
                            <Typography variant="body2" color="text.secondary">
                              No students enrolled in this class
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      // Desktop/Tablet View - Table
                      <Box sx={{
                        width: '100%',
                        overflow: 'auto',
                        maxHeight: isTablet ? '40vh' : 'none'
                      }}>
                        <DataTable><Table sx={{ minWidth: isTablet ? 600 : 'auto' }}>
                          <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                            <TableRow>
                              <TableCell sx={{
                                fontWeight: 'bold',
                                py: 2,
                                width: 60,
                                fontSize: isTablet ? '0.75rem' : '0.875rem'
                              }}>
                                #
                              </TableCell>
                              <TableCell sx={{
                                fontWeight: 'bold',
                                py: 2,
                                fontSize: isTablet ? '0.75rem' : '0.875rem'
                              }}>
                                STUDENT
                              </TableCell>
                              <TableCell sx={{
                                fontWeight: 'bold',
                                py: 2,
                                fontSize: isTablet ? '0.75rem' : '0.875rem'
                              }}>
                                DATE OF BIRTH
                              </TableCell>
                              <TableCell sx={{
                                fontWeight: 'bold',
                                py: 2,
                                fontSize: isTablet ? '0.75rem' : '0.875rem'
                              }}>
                                ADMISSION NO.
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cls.students && cls.students.map((student, index) => (
                              <TableRow
                                key={index}
                                onClick={() => handleOpenStudentModal(student)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                  }
                                }}
                              >
                                <TableCell sx={{ py: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {index + 1}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Stack direction="row" alignItems="center" spacing={2}>
                                    <StudentAvatar student={student}
                                      sx={{
                                        bgcolor: theme.palette.primary.main,
                                        width: 32,
                                        height: 32,
                                        fontSize: '0.875rem'
                                      }}
                                    >
                                      {getInitials(student.name || 'N/A')}
                                    </StudentAvatar>
                                    <Typography variant="body1" fontWeight="500">
                                      {student.name || 'N/A'}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Typography variant="body1">
                                    {student.dateOfBirth
                                      ? new Date(student.dateOfBirth).toLocaleDateString()
                                      : 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={student.admissionNumber || 'N/A'}
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table></DataTable>
                        {(!cls.students || cls.students.length === 0) && (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Person sx={{
                              fontSize: 48,
                              color: 'text.secondary',
                              opacity: 0.5,
                              mb: 1
                            }} />
                            <Typography variant="body1" color="text.secondary">
                              No students enrolled in this class
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      </Box>

      {/* Student Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseStudentModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <StudentAvatar student={selectedStudent} sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48, fontSize: '1.2rem' }}>
              {selectedStudent ? getInitials(selectedStudent.name) : 'S'}
            </StudentAvatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">{selectedStudent?.name || 'Student Profile'}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={selectedStudent?.admissionNumber || 'No Adm. No.'} size="small" color="primary" variant="outlined" />
                {selectedStudent?.gender && (
                  <Chip label={selectedStudent.gender} size="small" color={selectedStudent.gender === 'Male' ? 'info' : 'secondary'} variant="outlined" />
                )}
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={handleCloseStudentModal} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedStudent && (
            <Stack spacing={3}>

              {/* ── SECTION 1: Student Information ── */}
              <Box>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                  STUDENT INFORMATION
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {selectedStudent.email && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">EMAIL</Typography>
                      <Typography variant="body2" fontWeight="500">{selectedStudent.email}</Typography>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">DATE OF BIRTH</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {selectedStudent.dateOfBirth ? new Date(selectedStudent.dateOfBirth).toLocaleDateString() : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">CURRENT CLASS</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.currentClassName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">{selectedStudent.status === 'graduated' ? 'GRADUATION YEAR' : 'ACADEMIC YEAR'}</Typography>
                    <Typography variant="body2" fontWeight="500">{(selectedStudent.status === 'graduated' ? selectedStudent.graduationYear : selectedStudent.academicYear) || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">RELIGION</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.religion || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">HOME TOWN</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.hometown || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">LANGUAGE SPOKEN</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.languageSpoken || '—'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* ── SECTION 2: Parent/Guardian Information ── */}
              <Box>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                  PARENT / GUARDIAN INFORMATION
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">FATHER / GUARDIAN NAME</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.fatherName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">FATHER'S OCCUPATION</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.fatherOccupation || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">MOTHER / GUARDIAN NAME</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.motherName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">MOTHER'S OCCUPATION</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.motherOccupation || '—'}</Typography>
                  </Grid>
                  {(selectedStudent.guardianName && !selectedStudent.fatherName && !selectedStudent.motherName) && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">GUARDIAN NAME</Typography>
                        <Typography variant="body2" fontWeight="500">{selectedStudent.guardianName}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">GUARDIAN OCCUPATION</Typography>
                        <Typography variant="body2" fontWeight="500">{selectedStudent.guardianOccupation || '—'}</Typography>
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">PRIMARY CONTACT (PHONE 1)</Typography>
                    {selectedStudent.guardianPhone ? (
                      <Box
                        component="a"
                        href={`tel:${selectedStudent.guardianPhone}`}
                        sx={{ textDecoration: 'none', color: 'primary.main', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}
                      >
                        <Phone fontSize="small" /> {selectedStudent.guardianPhone}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>None</Typography>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">SECONDARY CONTACT (PHONE 2)</Typography>
                    {selectedStudent.guardianPhone2 ? (
                      <Box
                        component="a"
                        href={`tel:${selectedStudent.guardianPhone2}`}
                        sx={{ textDecoration: 'none', color: 'primary.main', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}
                      >
                        <Phone fontSize="small" /> {selectedStudent.guardianPhone2}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>None</Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCloseStudentModal} variant="outlined">
            Close
          </Button>
          {selectedStudent?.status !== 'graduated' && <Button
            onClick={handleOpenMoveDialog}
            variant="contained"
            startIcon={<SwapHorizIcon />}
            disabled={!selectedStudent}
          >
            Move to Class
          </Button>}
        </DialogActions>
      </Dialog>

      {/* Move Student Dialog */}
      <Dialog
        open={moveDialogOpen}
        onClose={handleCloseMoveDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon color="primary" /> Move Student to Class
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              This moves the student to the selected class and updates active-term enrollment records tied to the old class.
            </Alert>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                STUDENT
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {selectedStudent?.name || 'Student'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current class: {selectedStudent?.currentClassName || '—'}
              </Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel id="move-student-class-label">New Class</InputLabel>
              <Select
                labelId="move-student-class-label"
                value={targetClassId}
                label="New Class"
                onChange={(event) => setTargetClassId(event.target.value)}
              >
                {classes
                  .filter((cls) => String(cls._id) !== String(selectedStudent?.currentClassId || ''))
                  .map((cls) => (
                    <MenuItem key={cls._id} value={cls._id}>
                      {cls.classDisplayName || cls.displayName || cls.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseMoveDialog}
            disabled={movingStudent}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMoveStudent}
            disabled={movingStudent || !targetClassId}
            variant="contained"
          >
            {movingStudent ? <CircularProgress size={22} color="inherit" /> : 'Move Student'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Class Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteClassConfirm)}
        onClose={() => setDeletingClass(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.error.main }}>
          <DeleteIcon /> Confirm Deletion
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Are you absolutely sure you want to delete <strong>all students</strong> in the class <strong>{deleteClassConfirm?.classDisplayName}</strong>?
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 'bold' }}>
            Warning: This action is permanent. It will delete all their login accounts and their student records completely.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeleteClassConfirm(null)} 
            disabled={deletingClass}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteClassConfirm} 
            disabled={deletingClass}
            variant="contained" 
            color="error"
          >
            {deletingClass ? <CircularProgress size={24} color="inherit" /> : 'Yes, Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}

